import { FetchKeyedResource, FetchStateKeyedMixin, fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import { FetchState, FetchStateKey } from "@/shared/types";
import { ref, ToRefs } from "@vue/composition-api";
import { AxiosResponse } from "axios";
import { retryOnNotFoundError, RetryOptions } from "@/shared/retryHelper";

export function applyRefToState<TResource extends object> (emptyResourceGenerator: () => TResource): ToRefs<TResource> {
    const reffedState: Partial<ToRefs<TResource>> = {};
    const emptyState = emptyResourceGenerator();

    Object.keys(emptyState).forEach(propName => {
        reffedState[propName] = ref(emptyState[propName]);
    });

    return reffedState as ToRefs<TResource>;
}

export function addFetchStateMixin (): ToRefs<FetchStateKeyedMixin> {
    const fetchState = ref(FetchState.Uninitialized);
    const fetchKey = ref(null as (null | FetchStateKey));

    return {
        fetchState,
        fetchKey
    };
}

export function templatedFetchKeyStore<TResource extends object, TDto = TResource> (
    emptyResourceGenerator: () => TResource,
    dtoMapper: null | ((state: FetchKeyedResource<TResource>, dto: TDto) => void),
    apiCall: (username: string, partnerId: number, args?: any) => Promise<AxiosResponse<TDto>>,
    retryOptions?: RetryOptions,
    retryOn404?: boolean
) {
    const state = applyRefToState(emptyResourceGenerator);
    const mixin = addFetchStateMixin();

    function resetState () {
        const emptyState = emptyResourceGenerator();
        Object.keys(emptyState).forEach(propName => {
            state[propName].value = emptyState[propName];
        });
        mixin.fetchKey.value = null;
        mixin.fetchState.value = FetchState.Uninitialized;
    }

    function setLoading (fetchKey: FetchStateKey): void {
        mixin.fetchKey.value = fetchKey;
        mixin.fetchState.value = FetchState.Loading;
    }

    function setValueLoadedWithoutMapper ({ data, fetchKey }: { data: TDto, fetchKey: Partial<FetchStateKey> }): void {
        if (!fetchStateKeyMatch(mixin.fetchKey.value, fetchKey)) {
            return; // request is stale
        }

        mixin.fetchState.value = FetchState.Loaded;
        Object.keys(state).forEach(propName => {
            state[propName].value = data[propName];
        });
    }

    function setValueLoadedWithMapper ({ data, fetchKey }: { data: TDto, fetchKey: Partial<FetchStateKey> }): void {
        if (!fetchStateKeyMatch(mixin.fetchKey.value, fetchKey)) {
            return; // request is stale
        }

        mixin.fetchState.value = FetchState.Loaded;
        const mappedDto = {} as FetchKeyedResource<TResource>;
        dtoMapper(mappedDto, data);
        Object.keys(state).forEach(propName => {
            state[propName].value = mappedDto[propName];
        });
    }

    const setValueLoaded = dtoMapper !== null ? setValueLoadedWithMapper : setValueLoadedWithoutMapper;

    function invalidateLoadedState (newFetchKey: FetchStateKey): void {
        if (fetchStateKeyMatch(mixin.fetchKey.value, newFetchKey)) {
            return;
        }

        resetState();
    }

    /// ....

    function fetchKeyedApiCall (
        partialFetchKey: Partial<FetchStateKey>,
        args: any = undefined,
        force = false,
    ): Promise<AxiosResponse<TDto>> {

        partialFetchKey = partialFetchKey || {};
        const fetchKey: FetchStateKey = {
            username: partialFetchKey.username,
            partnerId: partialFetchKey.partnerId
        };

        invalidateLoadedState(fetchKey);

        if (!force && mixin.fetchState.value !== FetchState.Uninitialized) {
            return Promise.resolve(null);
        }

        setLoading(fetchKey);

        const actionResponse = retryOn404 ?
            retryOnNotFoundError(() =>
                    apiCall(fetchKey.username,
                        fetchKey.partnerId, args),
                retryOptions) : apiCall(fetchKey.username, fetchKey.partnerId, args);
        return actionResponse.then(response => {
            setValueLoaded({ fetchKey, data: response.data });
            return response;
        });
    }

    return {
        ...state,
        ...mixin,
        resetState,
        $reset: resetState,
        setLoading,
        setValueLoaded,
        invalidateLoadedState,
        fetchKeyedApiCall,
    };
}
